import { Clipboard } from "@angular/cdk/clipboard";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { posthog } from "posthog-js";
import { ConfirmationService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { CheckboxModule } from "primeng/checkbox";
import { ConfirmDialog } from "primeng/confirmdialog";
import { DialogModule } from "primeng/dialog";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { FluidModule } from "primeng/fluid";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { InputText } from "primeng/inputtext";
import { SelectModule } from "primeng/select";
import { Tooltip } from "primeng/tooltip";
import { finalize } from "rxjs";
import { EditAutomationDialogMode, EditAutomationUserDialogConfig } from "./edit-automation-user.dialog.types";
import { AccessRoleDetails } from "../../../../domain/access-role";
import { AutomationUserOverview } from "../../../../domain/automation-user";
import { OrganizationDetails } from "../../../../domain/organization";
import { UserDetails } from "../../../../domain/user";
import { OrganizationService } from "../../../../services/organization.service";
import { ToastService } from "../../../../services/toast.service";
import { primeNgPageModules } from "../../../../shared/modules/prime-ng-common.module";
import { reportToSentry } from "../../../../shared/sentry/reportFunctions";
import { DialogResult } from "../dialog.types";

/**
 * A dialog used for creating a new, or editing an existing, Automation User.
 */
@Component({
  templateUrl: "./edit-automation-user.dialog.html",
  imports: [
    ...primeNgPageModules,
    DialogModule,
    ButtonModule,
    FluidModule,
    TranslocoDirective,
    ConfirmDialog,
    ReactiveFormsModule,
    CheckboxModule,
    InputText,
    IconFieldModule,
    InputIconModule,
    FormsModule,
    SelectModule,
    Tooltip,
  ],
  providers: [ConfirmationService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditAutomationUserDialog {
  private organization: OrganizationDetails;

  public automationUser: AutomationUserOverview | null = null;
  public openingUser: UserDetails;
  public openingUserAccessRole: AccessRoleDetails | null;
  public accessRoles: AccessRoleDetails[];
  public dialogMode: EditAutomationDialogMode;
  public EditAutomationDialogModeEnum = EditAutomationDialogMode;
  public form: FormGroup<EditUserAutomationDialogForm>;
  public apiKey: string | undefined;
  private _loading = false;

  public get loading(): boolean {
    return this._loading;
  }

  public set loading(value: boolean) {
    this._loading = value;
    this.syncFormEnabledState();
    this.cdr.markForCheck();
  }

  constructor(
    public readonly ref: DynamicDialogRef,
    private readonly config: DynamicDialogConfig<EditAutomationUserDialogConfig>,
    private readonly organizationService: OrganizationService,
    private readonly translocoService: TranslocoService,
    private readonly toastService: ToastService,
    private readonly confirmationService: ConfirmationService,
    private readonly formBuilder: FormBuilder,
    private readonly clipboard: Clipboard,
    private readonly destroyRef: DestroyRef,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.organization = this.config.data!.organization;
    this.accessRoles = this.organization.accessRoles;
    this.dialogMode = this.config.data!.dialogMode;
    this.openingUser = this.config.data!.openingUser;

    const accessRole = this.openingUser.roleAssignments.find(
      (x) => x.organizationId === this.organization.id,
    )?.accessRole;
    this.openingUserAccessRole = accessRole ?? null;

    this.automationUser = this.config.data!.automationUser;
    this.form = this.formBuilder.group({
      name: new FormControl(this.automationUser?.fullName ?? "", [Validators.required]),
      isReadOnlyForExternals: new FormControl(this.automationUser?.isReadOnlyForExternals ?? false, {
        nonNullable: true,
      }),
      accessRoleId: new FormControl(this.automationUser?.roleAssignments?.[0]?.accessRole?.id ?? null, [
        Validators.required,
      ]),
    });

    this.syncFormEnabledState();
  }

  public submitEditForm(): void {
    this.form.updateValueAndValidity();

    if (!this.form.valid) {
      return;
    }

    // Massage isReadOnlyForExternals. We must not send it at *all* if we're not internal, or the API will reject it
    const isReadOnlyForExternals = this.openingUser.isInternal
      ? this.form.controls.isReadOnlyForExternals.value
      : undefined;

    let submissionObservable;
    if (this.dialogMode === EditAutomationDialogMode.Create) {
      submissionObservable = this.organizationService.createAutomationUser$(this.organization.id, {
        accessRoleId: this.form.controls.accessRoleId.value!,
        isReadOnlyForExternals: isReadOnlyForExternals,
        name: this.form.controls.name.value!,
      });
    } else {
      if (!this.automationUser) {
        // Shouldn't be possible to be in Edit mode without an AutomationUser, but let's make the compiler happy
        this.toastService.error(this.translocoService.translate("app.common.errors.unknown"));
        return;
      }
      // Edit mode
      submissionObservable = this.organizationService.updateAutomationUser$(
        this.organization.id,
        this.automationUser.id,
        {
          accessRoleId: this.form.controls.accessRoleId.value!,
          isReadOnlyForExternals: isReadOnlyForExternals,
          name: this.form.controls.name.value!,
        },
      );
    }

    this.loading = true;

    submissionObservable
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: (x) => {
          this.toastService.success(
            this.translocoService.translate("app.organization-settings.editAutomationUserDialog.success"),
          );
          if (this.dialogMode === EditAutomationDialogMode.Edit) {
            this.close({ shouldReload: true });
          } else {
            // If we've just created a user, leave the dialog open so they have a chance to copy the API key
            this.apiKey = x.freshApiKey;
            this.automationUser = x;
            this.dialogMode = EditAutomationDialogMode.Edit;
            this.form.markAsPristine();
            this.cdr.markForCheck();
          }
        },
        error: (err) => {
          this.toastService.error(
            this.translocoService.translate("app.organization-settings.editAutomationUserDialog.error"),
          );
          reportToSentry(err, "OrganizationSettings.EditAutomationUser");
        },
      });
  }

  public copyApiKeyToClipboard(): void {
    if (this.apiKey) {
      this.clipboard.copy(this.apiKey);
      this.toastService.success(
        this.translocoService.translate("app.organization-settings.editAutomationUserDialog.apiKeyCopied"),
      );
    }
  }

  public openRotateApiKeyConfirmationDialog(): void {
    this.confirmationService.confirm({
      header: this.translocoService.translate("app.organization-settings.editAutomationUserDialog.rotateApiKey"),
      message: this.translocoService.translate(
        "app.organization-settings.editAutomationUserDialog.rotateApiKeyConfirmation",
      ),
      icon: "pi pi-exclamation-triangle",
      rejectButtonProps: {
        label: this.translocoService.translate("app.common.cancel"),
        severity: "secondary",
        outlined: true,
      },
      acceptButtonProps: {
        label: this.translocoService.translate("app.common.confirm"),
        severity: "danger",
      },
      accept: () => {
        this.loading = true;

        this.organizationService
          .rotateAutomationUserApiKey$(this.organization.id, this.automationUser!.id)
          .pipe(
            finalize(() => {
              this.loading = false;
            }),
          )
          .subscribe({
            next: (x) => {
              this.toastService.success(
                this.translocoService.translate("app.organization-settings.editAutomationUserDialog.success"),
              );
              this.apiKey = x.freshApiKey;
              this.cdr.markForCheck();
            },
            error: (err) => {
              this.toastService.error(
                this.translocoService.translate("app.organization-settings.editAutomationUserDialog.error"),
              );
              reportToSentry(err, "OrganizationSettings.EditAutomationUser");
            },
          });
      },
    });
  }

  public openConfirmationDeleteDialog(): void {
    if (!this.shouldAllowDeletion()) {
      // The button shouldn't even be visible in this state, but let's be extra paranoid here
      return;
    }

    if (!this.automationUser) {
      // Shouldn't be possible to open this dialog without an AutomationUser, but let's make the compiler happy
      this.toastService.error(this.translocoService.translate("app.common.errors.unknown"));
      return;
    }
    const automationUser = this.automationUser;

    this.confirmationService.confirm({
      header: this.translocoService.translate("app.organization-settings.editAutomationUserDialog.deleteUser"),
      message: this.translocoService.translate(
        "app.organization-settings.editAutomationUserDialog.deleteUserConfirmation",
      ),
      icon: "pi pi-exclamation-triangle",
      rejectButtonProps: {
        label: this.translocoService.translate("app.common.cancel"),
        severity: "secondary",
        outlined: true,
      },
      acceptButtonProps: {
        label: this.translocoService.translate("app.common.delete"),
        severity: "danger",
      },
      accept: () => {
        this.loading = true;

        this.organizationService
          .deleteOrganizationUser$(this.organization.id, automationUser.id)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => {
              this.loading = false;
            }),
          )
          .subscribe({
            next: () => {
              this.toastService.success(
                this.translocoService.translate("app.organization-settings.editAutomationUserDialog.deleteUserSuccess"),
              );

              posthog.capture("user_deleted");

              this.close({ shouldReload: true });
            },
            error: (err) => {
              this.toastService.error(
                this.translocoService.translate("app.organization-settings.editAutomationUserDialog.error"),
              );
              reportToSentry(err, "OrganizationSettings.EditAutomationUser");
            },
          });
      },
    });
  }

  public shouldAllowDeletion(): boolean {
    if (this.automationUser?.isReadOnlyForExternals && !this.openingUser.isInternal) {
      return false;
    }
    // External users must have both `canModifyAutomationUsers` AND `canRemoveUsers` permissions in order to remove a user from an org.
    if (
      !this.openingUser.isInternal &&
      !(
        this.openingUserAccessRole?.has("canModifyAutomationUsers") && this.openingUserAccessRole?.has("canRemoveUsers")
      )
    ) {
      return false;
    }

    return true;
  }

  public shouldAllowEditing(): boolean {
    if (this.automationUser?.isReadOnlyForExternals && !this.openingUser.isInternal) {
      return false;
    }
    if (!this.openingUser.isInternal && !this.openingUserAccessRole?.has("canModifyAutomationUsers")) {
      return false;
    }
    return true;
  }

  private close(result: DialogResult): void {
    // Null out the API key, to make sure it gets cleaned out of memory (eventually)
    this.apiKey = undefined;
    this.ref.close(result);
  }

  private syncFormEnabledState(): void {
    if (this._loading || !this.shouldAllowEditing()) {
      this.form.disable({ emitEvent: false });
      return;
    }

    this.form.enable({ emitEvent: false });
    // Only internal users can modify the "isReadOnlyForExternals" field.
    if (!this.openingUser.isInternal) {
      this.form.controls.isReadOnlyForExternals.disable({ emitEvent: false });
    }
  }
}

interface EditUserAutomationDialogForm {
  name: FormControl<string | null>;
  isReadOnlyForExternals: FormControl<boolean>;
  accessRoleId: FormControl<number | null>;
}
